/**
 * Alarm management client (docs/control/API.md "Alarm management"; URS-CTL-022/023/043). Types mirror the server
 * payloads of apps/api/src/control/alarm-*.service.ts; nothing here invents a field.
 */
import { api } from "./api.js";
import type { AlarmRow, SigningInfo } from "./control-api.js";

export type Verdict = "PASS" | "WARN" | "FAIL" | "NO_DATA";
export interface Kpi {
  id: string;
  label: string;
  value: number;
  unit: string;
  target: string;
  verdict: Verdict;
}
export interface KpiReport {
  activations: number;
  kpis: Kpi[];
  topActors: { alarmId: string; count: number; sharePct: number }[];
  chattering: { alarmId: string; peakIn1Min: number }[];
  standing: { alarmId: string; sinceMs: number }[];
  stale: { alarmId: string; sinceMs: number }[];
  priorities: { priority: number; count: number; pct: number }[];
}
export interface KpiResponse {
  period: { from: string; to: string };
  source: string;
  plant: KpiReport;
  positions: Record<string, KpiReport>;
}
export interface FloodStatus {
  config: { windowS: number; threshold: number };
  flood: boolean;
  positions: { position: string; flood: boolean; since: string | null; alarmsInWindow: number }[];
  episodes: {
    episodeId: string;
    position: string;
    startedAt: string;
    endedAt: string | null;
    peakCount: number;
    threshold: number;
    windowS: number;
  }[];
}
export interface Overlay extends AlarmRow {
  byName?: string;
  at?: string;
  reason?: string;
  minutes?: number | null;
  suppressWhen?: string | null;
}
export interface Overlays {
  shelved: Overlay[];
  outOfService: Overlay[];
  suppressed: Overlay[];
}
export interface PriorityPolicy {
  priority: number;
  requiresSignature: boolean;
  maxShelveMin: number;
}
export interface MasterAlarm {
  unit: string;
  module: string;
  id: string;
  input: string;
  type: "HI" | "LO" | "DISCRETE";
  limit: number | null;
  deadband: number;
  onDelayS: number;
  offDelayS: number;
  priority: number;
  suppressWhen: string | null;
  consequence: string | null;
  response: string | null;
  variable: string;
  state: string;
  rationalised: boolean;
  rationalisedBy: string | null;
  rationalisedAt: string | null;
  changedSinceRationalised: boolean;
  note: string | null;
}
export interface MasterResponse {
  release: { id: string; no: number; hash: string };
  alarms: MasterAlarm[];
}
export interface Selector {
  unit?: string;
  type?: "HI" | "LO" | "DISCRETE";
  variable?: string;
  priority?: 1 | 2 | 3 | 4;
  ids?: string[];
}
export interface Change {
  limit?: number;
  limitOffset?: number;
  deadband?: number;
  onDelayS?: number;
  offDelayS?: number;
  priority?: 1 | 2 | 3 | 4;
  suppressWhen?: string | null;
  consequence?: string;
  response?: string;
}
export interface Proposal {
  selector: Selector;
  change: Change;
}
export type BulkResult =
  | { dryRun: true; changed: string[]; unchanged: string[] }
  | {
      dryRun: false;
      changed: string[];
      unchanged: string[];
      release: { id: string; release_no: number; status: string };
    };
export interface Scenario {
  id: string;
  label: string;
  description: string;
  durationS: number;
  units: string[];
}
export interface UpsetSummary {
  activations: number;
  peakIn10Min: number;
  floods: number;
  floodSeconds: number;
  byAlarm: { alarmId: string; count: number }[];
  skipped: string[];
}
export interface UpsetResponse {
  sandbox: true;
  simulated: true;
  scenario: { id: string; label: string; durationS: number };
  flood: { windowS: number; threshold: number };
  units: { unit: string; before: UpsetSummary; after: UpsetSummary | null }[];
}
export type ShelveResult =
  | { applied: true; minutes: number }
  | { applied: false; signatureRequired: true; signing: SigningInfo };
/** Policy, flood settings and out-of-service are signed changes: the answer is either done or a request to sign. */
export type SignedChange =
  | { applied: true }
  | { applied: false; signatureRequired: true; signing: SigningInfo };

const A = "/api/v1/control/alarms";
const enc = encodeURIComponent;

export const alarmMgmt = {
  kpis: (from: Date, to: Date) =>
    api.get<KpiResponse>(`${A}/kpis?from=${from.toISOString()}&to=${to.toISOString()}`),
  flood: () => api.get<FloodStatus>(`${A}/flood`),
  setFlood: (b: { windowS: number; threshold: number; reason: string }) =>
    api.post<SignedChange>(`${A}/flood-config`, b),
  overlays: () => api.get<Overlays>(`${A}/overlays`),
  policy: () => api.get<PriorityPolicy[]>(`${A}/policy`),
  setPolicy: (b: PriorityPolicy & { reason: string }) => api.post<SignedChange>(`${A}/policy`, b),
  shelve: (id: string, b: { reason: string; minutes: number }) =>
    api.post<ShelveResult>(`${A}/${enc(id)}/shelve`, b),
  unshelve: (id: string) => api.post<unknown>(`${A}/${enc(id)}/unshelve`),
  outOfService: (id: string, reason: string, hours: number) =>
    api.post<SignedChange>(`${A}/${enc(id)}/out-of-service`, { reason, hours }),
  returnToService: (id: string) => api.post<unknown>(`${A}/${enc(id)}/return-to-service`),
  signShelve: (requestId: string, b: { payloadHash: string; idToken: string }) =>
    api.post<unknown>(`${A}/requests/${requestId}/sign`, b),
  master: () => api.get<MasterResponse>(`${A}/master`),
  rationalise: (id: string, b: { rationalised: boolean; note: string }) =>
    api.post<MasterAlarm>(`${A}/master/${enc(id)}/rationalise`, b),
  bulk: (b: Proposal & { reason: string; changeControlRef: string; dryRun: boolean }) =>
    api.post<BulkResult>(`${A}/master/bulk`, b),
  scenarios: () => api.get<Scenario[]>(`${A}/master/scenarios`),
  upset: (b: { scenario: string; proposal?: Proposal }) =>
    api.post<UpsetResponse>(`${A}/master/upset-test`, b),
};
